import React, { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, setToken } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, Mail, Lock, User, Building2, Phone, ShieldCheck, RotateCcw, Eye, EyeOff } from "lucide-react";
import { validateEmail, validatePhone } from "../lib/validators";
import SmartLogo from "../components/SmartLogo";

const formatErr = (d) => {
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return d.msg || String(d);
};

/* ── Password strength ──────────────────────────────────────────────────── */
function getStrength(pw) {
  if (!pw) return { score: 0, label: "", color: "" };
  let score = 0;
  if (pw.length >= 5)  score++;
  if (pw.length >= 10) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;

  if (score <= 1) return { score, label: "Weak",   color: "bg-red-500" };
  if (score === 2) return { score, label: "Fair",   color: "bg-orange-400" };
  if (score === 3) return { score, label: "Good",   color: "bg-yellow-400" };
  if (score === 4) return { score, label: "Strong", color: "bg-green-500" };
  return              { score, label: "Very strong", color: "bg-emerald-500" };
}

function PasswordStrengthMeter({ password }) {
  const { score, label, color } = getStrength(password);
  if (!password) return null;

  const segments = 5;
  return (
    <div className="mt-1.5 space-y-1">
      <div className="flex gap-1">
        {Array.from({ length: segments }).map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-all duration-300 ${
              i < score ? color : "bg-zinc-200"
            }`}
          />
        ))}
      </div>
      <p className={`text-xs font-medium ${
        score <= 1 ? "text-red-600" :
        score === 2 ? "text-orange-500" :
        score === 3 ? "text-yellow-600" :
        "text-green-600"
      }`}>
        {label}
      </p>
    </div>
  );
}

/* ── OTP digit entry ────────────────────────────────────────────────────── */
function OtpInput({ value, onChange }) {
  const digits = value.split("");

  const handleKey = (e, idx) => {
    const key = e.key;
    if (key === "Backspace") {
      if (digits[idx]) {
        const next = [...digits];
        next[idx] = "";
        onChange(next.join(""));
      } else if (idx > 0) {
        const next = [...digits];
        next[idx - 1] = "";
        onChange(next.join(""));
        document.getElementById(`otp-${idx - 1}`)?.focus();
      }
      return;
    }
    if (!/^\d$/.test(key)) return;
    const next = [...digits];
    next[idx] = key;
    onChange(next.join(""));
    if (idx < 5) document.getElementById(`otp-${idx + 1}`)?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted) { onChange(pasted.padEnd(6, "").slice(0, 6)); }
  };

  return (
    <div className="flex gap-2 justify-center" onPaste={handlePaste}>
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          id={`otp-${i}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i] || ""}
          onKeyDown={(e) => handleKey(e, i)}
          onChange={() => {}} // controlled via onKeyDown
          onClick={() => document.getElementById(`otp-${i}`)?.select()}
          data-testid={`otp-digit-${i}`}
          className="w-11 h-12 text-center text-xl font-bold border border-zinc-200 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-1 transition"
        />
      ))}
    </div>
  );
}

/* ── Main SignUp component ─────────────────────────────────────────────── */
export default function SignUp() {
  const navigate = useNavigate();

  // Step 1 — form data
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", password: "" });
  const [phoneDigits, setPhoneDigits] = useState("");
  const [emailError, setEmailError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [step1Busy, setStep1Busy] = useState(false);

  // Step 2 — OTP verification
  const [step, setStep] = useState(1); // 1 = form, 2 = verify
  const [otp, setOtp] = useState("");
  const [step2Busy, setStep2Busy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const update = (k) => (v) => {
    setForm(p => ({ ...p, [k]: v }));
    if (k === "email") setEmailError(null);
  };

  const handlePhoneChange = (v) => {
    const digits = v.replace(/\D/g, "").slice(0, 10);
    setPhoneDigits(digits);
    setPhoneError(null);
  };

  /* Step 1: validate + request OTP */
  const submitStep1 = async (e) => {
    e.preventDefault();

    const emailErr = validateEmail(form.email);
    const fullPhone = phoneDigits ? `+1${phoneDigits}` : "";
    const phoneErr = phoneDigits.length > 0 && phoneDigits.length !== 10
      ? "Phone number must be exactly 10 digits."
      : validatePhone(fullPhone);

    if (emailErr) { setEmailError(emailErr); toast.error(emailErr); return; }
    if (phoneErr) { setPhoneError(phoneErr); toast.error(phoneErr); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }

    setStep1Busy(true);
    try {
      const payload = { ...form, phone: fullPhone };
      await api.post("/auth/signup/request-otp", payload);
      toast.success("Verification code sent — check your inbox.");
      setStep(2);
      startResendCooldown();
      // Auto-focus first OTP digit
      setTimeout(() => document.getElementById("otp-0")?.focus(), 100);
    } catch (err) {
      const status = err.response?.status;
      const detail = formatErr(err.response?.data?.detail) || "Could not send code.";
      if (status === 409) {
        // Email already registered — show inline error under the email field
        setEmailError("An account with this email already exists.");
        toast.error(
          <span>
            An account with this email already exists.{" "}
            <a href="/login" className="underline font-semibold">Sign in instead?</a>
          </span>
        );
      } else {
        toast.error(detail);
      }
    } finally { setStep1Busy(false); }
  };

  /* Step 2: verify OTP and finish signup */
  const submitStep2 = async (e) => {
    e.preventDefault();
    if (otp.replace(/\D/g, "").length < 6) { toast.error("Enter all 6 digits."); return; }

    setStep2Busy(true);
    try {
      const res = await api.post("/auth/signup/verify-otp", {
        email: form.email,
        code: otp,
      });
      if (res.data.session_token) setToken(res.data.session_token); // mobile cookie fallback
      toast.success("Account created. Welcome!");
      navigate("/dashboard", { state: { user: res.data.user } });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Verification failed.");
    } finally { setStep2Busy(false); }
  };

  /* Resend with 60 s cooldown */
  const startResendCooldown = useCallback(() => {
    setResendCooldown(60);
    const id = setInterval(() => {
      setResendCooldown(c => {
        if (c <= 1) { clearInterval(id); return 0; }
        return c - 1;
      });
    }, 1000);
  }, []);

  const resendCode = async () => {
    if (resendCooldown > 0) return;
    try {
      const fullPhone = phoneDigits ? `+1${phoneDigits}` : "";
      await api.post("/auth/signup/request-otp", { ...form, phone: fullPhone });
      toast.success("New code sent.");
      setOtp("");
      startResendCooldown();
      setTimeout(() => document.getElementById("otp-0")?.focus(), 100);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not resend code.");
    }
  };

  /* ── Render ── */
  return (
    <div className="min-h-screen flex bg-[#FDFDFD] px-6 sm:px-12 py-10 lg:py-14" data-testid="signup-page">
      <div className="w-full max-w-xl mx-auto">

        {/* Logo */}
        <div className="mb-12">
          <SmartLogo />
        </div>

        {step === 1 && (
          <>
            <span className="label-eyebrow">Get started</span>
            <h1 className="font-display text-4xl font-bold tracking-tight mt-3 leading-[1.05]">
              Create your account.
            </h1>
            <p className="text-sm text-zinc-600 mt-2">Start tracking permits in under a minute.</p>

            <form onSubmit={submitStep1} className="mt-8 space-y-4" data-testid="signup-form">
              <Field icon={User} label="Full name" type="text" value={form.name} onChange={update("name")} placeholder="Jane Doe" testId="signup-name" required />
              <Field icon={Building2} label="Company" type="text" value={form.company} onChange={update("company")} placeholder="Acme Construction" testId="signup-company" />

              {/* Phone */}
              <div>
                <label className="label-eyebrow block mb-1.5">Phone</label>
                <div className="flex gap-2">
                  <div className="flex items-center px-3 py-2.5 border border-zinc-200 rounded-sm bg-zinc-50 text-sm text-zinc-500 font-medium select-none whitespace-nowrap">
                    +1
                  </div>
                  <div className="relative flex-1">
                    <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <input
                      type="tel" value={phoneDigits} onChange={e => handlePhoneChange(e.target.value)}
                      placeholder="5555550100" maxLength={10} data-testid="signup-phone"
                      className={`w-full pl-9 pr-3 py-2.5 border rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                        phoneError ? "border-red-400 focus:ring-red-400" : "border-zinc-200 focus:ring-zinc-950"
                      }`}
                    />
                  </div>
                </div>
                {phoneError && <p className="mt-1 text-xs text-red-600" data-testid="signup-phone-error">{phoneError}</p>}
              </div>

              <Field icon={Mail} label="Email" type="email" value={form.email} onChange={update("email")} placeholder="you@company.com" testId="signup-email" required error={emailError} />

              {/* Password with strength meter */}
              <div>
                <label className="label-eyebrow block mb-1.5">Password</label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <input
                    type={showPw ? "text" : "password"} required value={form.password}
                    onChange={e => update("password")(e.target.value)}
                    placeholder="At least 8 characters"
                    data-testid="signup-password"
                    className="w-full pl-9 pr-9 py-2.5 border border-zinc-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-1"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 transition-colors"
                    tabIndex={-1}
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <PasswordStrengthMeter password={form.password} />
              </div>

              <button type="submit" disabled={step1Busy} data-testid="signup-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-zinc-950 text-white font-semibold px-5 py-3 rounded-sm hover:bg-zinc-800 disabled:opacity-50 transition-colors">
                {step1Busy ? "Sending code…" : "Continue"} <ArrowRight size={16} />
              </button>

              <p className="text-xs text-zinc-500 text-center leading-relaxed">
                By creating an account you agree to our{" "}
                <Link to="/terms" target="_blank" className="underline hover:text-zinc-900">Terms of Service</Link>{" "}
                and{" "}
                <Link to="/privacy" target="_blank" className="underline hover:text-zinc-900">Privacy Policy</Link>.
              </p>

              <p className="text-sm text-zinc-600 text-center">
                Already have an account?{" "}
                <Link to="/login" data-testid="back-to-login-link" className="font-semibold text-zinc-950 underline">Sign in</Link>
              </p>
            </form>
          </>
        )}

        {step === 2 && (
          <>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <ShieldCheck size={20} className="text-amber-600" />
              </div>
              <div>
                <span className="label-eyebrow block">Verify your email</span>
                <p className="text-sm text-zinc-600 mt-0.5">
                  We sent a 6-digit code to <span className="font-semibold text-zinc-950">{form.email}</span>
                </p>
              </div>
            </div>

            <h1 className="font-display text-3xl font-bold tracking-tight leading-[1.05] mb-8">
              Enter the code.
            </h1>

            <form onSubmit={submitStep2} className="space-y-6" data-testid="otp-form">
              <OtpInput value={otp} onChange={setOtp} />

              <button type="submit" disabled={step2Busy || otp.length < 6} data-testid="otp-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-zinc-950 text-white font-semibold px-5 py-3 rounded-sm hover:bg-zinc-800 disabled:opacity-50 transition-colors">
                {step2Busy ? "Verifying…" : "Create account"} <ArrowRight size={16} />
              </button>

              <div className="flex items-center justify-between text-sm pt-1">
                <button
                  type="button"
                  onClick={() => { setStep(1); setOtp(""); }}
                  className="text-zinc-500 hover:text-zinc-950 flex items-center gap-1 underline"
                >
                  ← Change email
                </button>

                <button
                  type="button"
                  onClick={resendCode}
                  disabled={resendCooldown > 0}
                  className="inline-flex items-center gap-1 text-zinc-600 hover:text-zinc-950 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <RotateCcw size={13} />
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                </button>
              </div>
            </form>

            <p className="mt-8 text-xs text-zinc-500 text-center">
              Didn't get the email? Check your spam folder, or click resend above.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const Field = ({ icon: Icon, label, type, value, onChange, placeholder, testId, required, error }) => (
  <div>
    <label className="label-eyebrow block mb-1.5">{label}</label>
    <div className="relative">
      {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />}
      <input
        type={type} required={required} value={value}
        onChange={e => onChange(e.target.value)} placeholder={placeholder}
        data-testid={testId}
        className={`w-full ${Icon ? "pl-9" : "pl-3"} pr-3 py-2.5 border rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-offset-1 ${
          error ? "border-red-400 focus:ring-red-400" : "border-zinc-200 focus:ring-zinc-950"
        }`}
      />
    </div>
    {error && <p className="mt-1 text-xs text-red-600" data-testid={`${testId}-error`}>{error}</p>}
  </div>
);
